import React, { useState } from 'react';

// Actions =>
import { UUID } from '@Src/Global/Actions';
import { handleExpandPanel } from '@Src/Cart/Actions';

// Redux =>
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

// Components =>
import Template from '@Src/Cart/Template';
import Button from '@Shared/Button';
import Loading from '@Shared/Loading';
import Summary from '@Src/Cart/Summary';
import Motivation from '@Src/Cart/Motivation';
import Minicart from '@Src/Cart/Minicart';
import Addresses from '@Src/Addresses/Addresses';
import Stores from '@Src/Stores/Stores';
import CardTransfer from '@Src/Cart/CardTransfer';

// Routing
import { useNavigate } from 'react-router-dom';

// Toast
import { toast } from 'react-toastify';


const Step1 = ({ cart: { expandedPanel, delivery, basket }, global: { client, store }, handleExpandPanel }) => {
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [paymentLocal, setPaymentLocal] = useState(false);
    const useLocalPayment = () => {
        setPaymentLocal(true);
        setSubmitting(true);
    }

    const handleNavigate = (path) => {
        if (expandedPanel) {
            handleExpandPanel();
        }
        navigate(path);
    }

    const handleSubmit = async () => {
        if (!delivery) {
            toast.error('Es necesario seleccionar una dirección de entrega o tienda para recolección.');
            return;
        }

        if (!basket || !basket.sum_primary) {
            toast.error('No fue posible obtener el total de tu compra.');
            return;
        }

        if (!client || !client.data) {
            toast.error('Debes iniciar sesión para continuar con el pago.');
            return;
        }

        const amount = parseFloat(basket.sum_primary);

        if (Number.isNaN(amount) || amount <= 0) {
            toast.error('El total actual no es válido para procesar el pago.');
            return;
        }

        const successUrl = process.env.REACT_APP_MERCADOPAGO_SUCCESS_URL || `${window.location.origin}/cart/step-4?status=success`;
        const failureUrl = process.env.REACT_APP_MERCADOPAGO_FAILURE_URL || `${window.location.origin}/cart/step-4?status=failure`;
        const pendingUrl = process.env.REACT_APP_MERCADOPAGO_PENDING_URL || `${window.location.origin}/cart/step-4?status=pending`;

        const fallbackTitle = basket.id ? `Pedido #${basket.id}` : 'Pedido';

        const items = (basket.items || []).map((item) => {
            const quantity = Number(item.quantity || item.qty || item.pieces || 1);
            const unitPrice = Number(item.discount || item.price || item.unit_price || 0);

            return {
                title: item.description || item.name || fallbackTitle,
                quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
                unit_price: Number.isFinite(unitPrice) && unitPrice > 0 ? Number(unitPrice.toFixed(2)) : 0,
                currency_id: 'MXN',
            };
        }).filter((item) => item.unit_price > 0);

        const payload = {
            items: items.length ? items : [{
                title: fallbackTitle,
                quantity: 1,
                unit_price: Number(amount.toFixed(2)),
                currency_id: 'MXN',
            }],
            back_urls: {
                success: successUrl,
                failure: failureUrl,
                pending: pendingUrl,
            },
            auto_return: 'approved',
        };

        localStorage.setItem('checkoutDelivery', JSON.stringify(delivery));
        localStorage.setItem('checkoutBasket', JSON.stringify(basket));

        setSubmitting(true);

        try {
            const response = await fetch('https://businext.com.mx/service/admin/payment/mercadopago/checkout', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                const description = data?.description || data?.message || 'No fue posible crear el checkout.';
                throw new Error(description);
            }
            const checkoutLink =
                data?.initPoint;
            // data?.sandboxInitPoint;

            if (checkoutLink) {
                window.location.replace(checkoutLink);
            } else {
                throw new Error('No se recibió el enlace de pago de Mercado Pago.');
            }
        } catch (error) {
            const message = error?.message || 'No fue posible iniciar el pago. Intenta nuevamente.';
            toast.error(message);
        }
    }

    return (
        !submitting ?
            <Template
                title={'Selecciona el lugar de entrega'}
                className={'cart-step-2'}
                step={1}
                left={[
                    <Addresses selectable={true} key={UUID()} title={'Entrega a domicilio'} />,
                    <h2 key={UUID()}>Ó recoger en sucursal</h2>,
                    <Stores key={UUID()} />
                ]}
                right={[
                    <Minicart key={UUID()} />,
                    <Summary key={UUID()} />,
                    <Motivation key={UUID()} />,
                    <div className={'buttons-holder'} key={UUID()}>
                        {delivery ?
                            <>
                                <p>Finaliza tu compra con una de las siguientes opciones de pago</p>
                                <Button onClick={handleSubmit} variant={'primary'}>Mercado Pago</Button>
                                <p className={'or'}>O</p>
                                <Button onClick={useLocalPayment} variant={'primary'}>Transferencia / Depósito</Button>
                            </>
                            : null}
                        <Button onClick={() => handleNavigate('/cart')} variant={'link'}>Regresar</Button>
                        <Button onClick={handleExpandPanel} variant={'link'}>Ver resumen</Button>
                    </div>
                ]}
            />
            :
            !paymentLocal ?
                <Loading text="Te redirigiremos a Mercado Pago para que completes tu pago y al finalizar volverás automáticamente a nuestro sitio." />
                :
                <CardTransfer cancel={() => {
                    setSubmitting(false);
                    setPaymentLocal(false);
                }} handleSubmit={() => {
                    navigate('/cart/step-4?status=success');
                }} />
    );
};

// mapStateToProps =>
function mapStateToProps(state) {
    return state;
}

// Bind actions to be used along redux =>
function matchDispatchToProps(dispatch) {
    return bindActionCreators({
        // Actions
        handleExpandPanel,
    }, dispatch);
}

export default connect(mapStateToProps, matchDispatchToProps)(Step1);
